import { useState, useEffect, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Utilitaires pour la gestion de session
const SESSION_KEY = 'simblyo_session';
const USER_KEY = 'simblyo_user';

const saveSession = (token, user) => {
  try {
    localStorage.setItem(SESSION_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch (e) {
    console.warn('localStorage not available');
  }
};

const getStoredSession = () => {
  try {
    return {
      token: localStorage.getItem(SESSION_KEY),
      user: JSON.parse(localStorage.getItem(USER_KEY) || 'null')
    };
  } catch (e) {
    return { token: null, user: null };
  }
};

const clearSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(USER_KEY);
  } catch (e) {
    console.warn('localStorage not available');
  }
};

export default function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    try {
      // Essayer d'abord avec le cookie
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true,
        timeout: 15000
      });
      
      setUser(response.data);
      setIsAuthenticated(true);
      
      // Sauvegarder en localStorage pour les PWA iOS
      saveSession('cookie', response.data);
      
      // Rafraîchir la session en arrière-plan (prolonger de 30 jours)
      axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, { 
        withCredentials: true,
        timeout: 10000
      }).catch(() => {});
      
    } catch (error) {
      console.error('Auth check failed:', error);
      
      // Si erreur réseau (pas 401), utiliser le cache local
      if (!error.response && retryCount < 2) {
        const stored = getStoredSession();
        if (stored.user) {
          console.log('Using cached session');
          setUser(stored.user);
          setIsAuthenticated(true);
          setRetryCount(prev => prev + 1);
          return;
        }
      }
      
      // Session vraiment expirée ou invalide
      if (error.response?.status === 401) {
        clearSession();
      }
      
      setIsAuthenticated(false);
    }
  }, [retryCount]);

  useEffect(() => {
    checkAuth();
    
    // Revérifier quand l'app revient au premier plan (PWA)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        checkAuth();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkAuth, isAuthenticated]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
}

// Export pour utilisation dans d'autres composants
export { saveSession, getStoredSession, clearSession };
